/**
 * frameSimilarity — lightweight pixel-diff for consecutive frame comparison.
 *
 * Downsamples the capture canvas to a tiny 64×48 image and computes
 * mean absolute difference (MAD) across RGB channels vs the previous frame.
 * Returns a similarity score 0 (completely different) to 1 (identical).
 *
 * At 64×48 = 3,072 pixels × 3 channels = ~9,216 comparisons — sub-millisecond.
 */

const COMPARE_WIDTH = 64
const COMPARE_HEIGHT = 48

/** Lazily-created comparison canvas (reused across calls). */
let compareCanvas: HTMLCanvasElement | null = null
let compareCtx: CanvasRenderingContext2D | null = null

function getCompareCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  if (!compareCanvas) {
    compareCanvas = document.createElement('canvas')
    compareCanvas.width = COMPARE_WIDTH
    compareCanvas.height = COMPARE_HEIGHT
    compareCtx = compareCanvas.getContext('2d', { willReadFrequently: true })!
  }
  return { canvas: compareCanvas, ctx: compareCtx! }
}

export interface SimilarityResult {
  /** 0 = completely different, 1 = identical */
  similarity: number
  /** ImageData of the current frame (pass as prevImageData on next call) */
  currentImageData: ImageData
}

/**
 * Compute similarity between the current canvas content and the previous frame.
 *
 * @param sourceCanvas - The low-res capture canvas (any size, will be downsampled)
 * @param prevImageData - ImageData from the previous call, or null for the first frame
 * @returns SimilarityResult with score and currentImageData for next comparison
 */
export function computeSimilarity(
  sourceCanvas: HTMLCanvasElement,
  prevImageData: ImageData | null,
): SimilarityResult {
  const { ctx } = getCompareCanvas()

  // Downsample source canvas → 64×48
  ctx.drawImage(sourceCanvas, 0, 0, COMPARE_WIDTH, COMPARE_HEIGHT)
  const currentImageData = ctx.getImageData(0, 0, COMPARE_WIDTH, COMPARE_HEIGHT)

  if (!prevImageData) {
    // First frame — nothing to compare against
    return { similarity: 0, currentImageData }
  }

  const curr = currentImageData.data
  const prev = prevImageData.data
  const pixelCount = COMPARE_WIDTH * COMPARE_HEIGHT

  let totalDiff = 0
  for (let i = 0; i < curr.length; i += 4) {
    // Compare RGB channels (skip alpha at i+3)
    totalDiff += Math.abs(curr[i] - prev[i])       // R
    totalDiff += Math.abs(curr[i + 1] - prev[i + 1]) // G
    totalDiff += Math.abs(curr[i + 2] - prev[i + 2]) // B
  }

  // Normalize: max possible diff = pixelCount × 3 channels × 255
  const maxDiff = pixelCount * 3 * 255
  const similarity = 1 - totalDiff / maxDiff

  return { similarity, currentImageData }
}
